import { api, enc, qs } from '../api';
import { clean, fmtDate, fmtWhen, num } from '../format';
import { go } from '../router';
import { useApp, useLoad } from '../state';
import { useFeedback } from '../ui/feedback';
import { Area, Choice, Grid, Select, Text, opts, words } from '../ui/fields';
import { Empty, Loading, PageHead, Ref, Sub, Tabs } from '../ui/bits';

// Phase 3: the web registry. Pages and forms the beacon and relay will serve, tracked go-links, submissions.
type Tab = 'pages' | 'forms' | 'links' | 'submissions' | 'snippet';
const TABS: Array<[Tab, string]> = [['pages', 'Pages'], ['forms', 'Forms'], ['links', 'Links'], ['submissions', 'Submissions'], ['snippet', 'Snippet']];
const STATUS3 = words(['draft', 'live', 'retired']);
const SUBMIT = words(['emm_relay', 'hubspot_form', 'mailto', 'external', 'ui_only']);
const HOSTING = words(['cloudflare_pages', 'cloudflare_worker', 'hubspot', 'external']);
const MEDIUMS = words(['email', 'social', 'paid', 'referral', 'organic', 'print', 'event', 'qr']); // organic: asked for by Julian, 2026-10-20

/** Resolve a typed TID to its track id (pages and forms store the id). */
async function trackIdFor(tid: unknown) {
  if (!tid) return null;
  const r = await api('GET', `/tracks/${enc(String(tid).trim())}`);
  return r.track.id as string;
}

function CopyLink({ url }: { url: string }) {
  const { toast } = useFeedback();
  return <button className="quiet copy" type="button" onClick={() => navigator.clipboard.writeText(url).then(() => toast('Link copied'), () => toast(url))}>Copy</button>;
}

export function useWebDialogs() {
  const { vocab } = useApp();
  const { openDialog, toast } = useFeedback();

  const newProperty = (onDone: () => void) => openDialog({
    title: 'New web property', okLabel: 'Create',
    body: (<>
      <Grid><Select name="brand_code" label="Brand" options={opts(vocab.brand)} value="3HUE" /><Select name="hosting" label="Hosting" options={HOSTING} value="cloudflare_pages" /></Grid>
      <Text name="name" label="Name" required placeholder="3hue.net" />
      <Text name="base_url" label="Base URL" required placeholder="https://3hue.net" />
      <p className="muted small">The beacon and form relay accept requests only from registered properties (CORS).</p>
    </>),
    onSubmit: async (v) => { await api('POST', '/web/properties', clean(v)); toast('Property created'); onDone(); },
  });

  const newPage = (properties: any[], onDone: () => void, tid?: string) => openDialog({
    title: 'Register a page', okLabel: 'Register',
    body: (<>
      <Grid><Select name="property_id" label="Property" options={opts(properties, 'name', 'id', false)} /><Select name="page_type_code" label="Type" options={opts(vocab.page_type)} blank="—" /></Grid>
      <Text name="url" label="URL" required placeholder="https://3hue.net/lp/risk-assessment" />
      <Grid><Text name="title" label="Title" required /><Text name="path" label="Path (blank = from the URL)" /></Grid>
      <Grid cols={3}><Text name="tid" label="Track (TID)" value={tid} placeholder="SI.WEB.PSPCT.26.001-03" /><Select name="status" label="Status" options={STATUS3} value="live" /><Select name="is_landing_page" label="Landing page" options={[{ value: '0', label: 'No' }, { value: '1', label: 'Yes (campaign-built)' }]} value={tid ? '1' : '0'} /></Grid>
      <Select name="telemetry_enabled" label="Telemetry" options={[{ value: '1', label: 'On — the beacon counts this path' }, { value: '0', label: 'Off' }]} value="1" />
      <Area name="summary" label="Summary" />
    </>),
    onSubmit: async (v) => {
      const track_id = await trackIdFor(v.tid);
      const body: Record<string, any> = { ...clean(v), track_id, is_landing_page: num(v.is_landing_page), telemetry_enabled: num(v.telemetry_enabled) }; delete body.tid;
      await api('POST', '/web/pages', body); toast('Page registered'); onDone();
    },
  });

  const newForm = (pages: any[], onDone: () => void, tid?: string) => openDialog({
    title: 'Register a form', okLabel: 'Register',
    body: (<>
      <Select name="page_id" label="On page" options={pages.map((p) => ({ value: p.id, label: `${p.property_name}${p.path} — ${p.title}` }))} />
      <Grid><Text name="name" label="Form name" required placeholder="Risk assessment request" /><Text name="tid" label="Track (TID)" value={tid} /></Grid>
      <Grid><Select name="submit_method" label="Submits via" options={SUBMIT} value="emm_relay" /><Text name="hubspot_form_guid" label="HubSpot form GUID (relay target)" /></Grid>
      <Grid><Select name="status" label="Status" options={STATUS3} value="live" /><Text name="purpose" label="Purpose" /></Grid>
      <p className="muted small">With <code>emm_relay</code> the page posts to <span className="mono">/f/&lt;form id&gt;</span>; the Worker stores the submission and relays it to HubSpot with the MID and TID attached.</p>
    </>),
    onSubmit: async (v) => {
      const track_id = await trackIdFor(v.tid);
      const body: Record<string, any> = { ...clean(v), track_id }; delete body.tid;
      await api('POST', '/web/forms', body); toast('Form registered'); onDone();
    },
  });

  const newLink = (onDone: (url: string) => void, preset: { tid?: string; mid?: string } = {}) => openDialog({
    title: 'Create a tracked link', okLabel: 'Create link',
    body: (<>
      <p className="muted small">A short link on the public host. The redirect adds utm_campaign = MID, utm_content = TID, plus emm_mid and emm_tid.</p>
      <Grid><Text name="tid" label="Track (TID)" value={preset.tid} placeholder="SI.EML.PSPCT.26.001-01" /><Text name="mid" label="…or campaign (MID) only" value={preset.tid ? '' : preset.mid} /></Grid>
      <Text name="destination_url" label="Destination URL" required placeholder="https://3hue.net/lp/risk-assessment" />
      <Grid cols={3}><Text name="utm_source" label="utm_source" required placeholder="hubspot" /><Select name="utm_medium" label="utm_medium" options={MEDIUMS} value="email" /><Text name="utm_term" label="utm_term" /></Grid>
      <Grid><Text name="label" label="Label" placeholder="Intro email — primary CTA" /><Text name="code" label="Custom code (optional)" placeholder="risk-26" /></Grid>
    </>),
    onSubmit: async (v) => { const r = await api('POST', '/web/links', clean(v)); toast(`Created ${r.url}`); onDone(r.url); },
  });

  // Your own artifacts (0066): the person who added a link or a page edits and removes it; so do the campaign's editors and managers.
  const editLink = (l: any, onDone: () => void) => openDialog({
    title: `Edit /go/${l.code}`, okLabel: 'Save',
    body: (<>
      <Text name="destination_url" label="Destination URL" required value={l.destination_url} />
      <Grid><Text name="label" label="Label" value={l.label ?? ''} /><Text name="utm_term" label="utm_term" value={l.utm_term ?? ''} /></Grid>
      <Select name="is_active" label="Active (an inactive link answers 404)" options={[{ value: '1', label: 'Yes' }, { value: '0', label: 'No' }]} value={String(l.is_active ?? 1)} />
      <p className="muted small">utm_source and utm_medium stay as minted: changing them would split the numbers the link has earned. Make a new link for a new source.</p>
    </>),
    onSubmit: async (v) => { await api('PATCH', `/web/links/${enc(l.code)}`, { destination_url: v.destination_url, label: v.label || null, utm_term: v.utm_term || null, is_active: Number(v.is_active) }); toast('Link saved'); onDone(); },
  });
  const removeLink = async (l: any, onDone: () => void) => {
    if (!confirm(`Remove /go/${l.code}${l.click_count ? ` (it has ${l.click_count} clicks on record, so it will be switched off instead)` : ''}?`)) return;
    try { const r = await api('DELETE', `/web/links/${enc(l.code)}`); toast(r.deactivated ? r.why : 'Link removed'); onDone(); } catch (e) { toast((e as Error).message, true); }
  };
  const editPage = (p: any, onDone: () => void) => openDialog({
    title: `Edit ${p.path}`, okLabel: 'Save',
    body: (<>
      <Text name="title" label="Title" required value={p.title} />
      <Grid><Select name="page_type_code" label="Type" options={opts(vocab.page_type)} value={p.page_type_code ?? ''} blank="—" /><Select name="status" label="Status" options={STATUS3} value={p.status} /></Grid>
      <Grid><Select name="is_landing_page" label="Landing page" options={[{ value: '1', label: 'Yes' }, { value: '0', label: 'No' }]} value={String(p.is_landing_page ?? 0)} /><Select name="telemetry_enabled" label="Telemetry" options={[{ value: '1', label: 'On' }, { value: '0', label: 'Off' }]} value={String(p.telemetry_enabled ?? 1)} /></Grid>
      <Area name="summary" label="Summary (what the page is for; the agents read it)" value={p.summary ?? ''} rows={2} />
    </>),
    onSubmit: async (v) => { await api('PATCH', `/web/pages/${enc(p.id)}`, { title: v.title, page_type_code: v.page_type_code || null, status: v.status, is_landing_page: Number(v.is_landing_page), telemetry_enabled: Number(v.telemetry_enabled), summary: v.summary || null }); toast('Page saved'); onDone(); },
  });
  const removePage = async (p: any, onDone: () => void) => {
    if (!confirm(`Remove ${p.path} from the registry?${p.form_count || p.submissions_30d ? ' It has forms or submissions, so it will be retired instead.' : ''}`)) return;
    try { const r = await api('DELETE', `/web/pages/${enc(p.id)}`); toast(r.retired ? r.why : 'Page removed'); onDone(); } catch (e) { toast((e as Error).message, true); }
  };

  return { newProperty, newPage, newForm, newLink, editLink, removeLink, editPage, removePage };
}

export function Web({ query }: { query: Record<string, string> }) {
  const tab = (query.tab as Tab) || 'pages';
  return (<>
    <PageHead title="Web" sub="Pages and forms the beacon and relay serve, tracked links, and what visitors submitted" />
    <Tabs items={TABS} current={tab} onPick={(t) => go(`#/web?tab=${t}`)} />
    {tab === 'pages' && <PagesTab q={query.q} />}
    {tab === 'forms' && <FormsTab />}
    {tab === 'links' && <LinksTab q={query.q} />}
    {tab === 'submissions' && <SubmissionsTab status={query.status} />}
    {tab === 'snippet' && <SnippetTab />}
  </>);
}

function PagesTab({ q }: { q?: string }) {
  const { can } = useApp();
  const { newProperty, newPage, editPage, removePage } = useWebDialogs();
  const { data, error, reload } = useLoad(() => Promise.all([api('GET', '/web/properties'), api('GET', `/web/pages?${qs({ q })}`)]), [q]);
  if (error) return <Empty>{error}</Empty>;
  if (!data) return <Loading />;
  const [{ properties }, { pages }] = data;
  return (<>
    <div className="toolbar">
      <form onSubmit={(ev) => { ev.preventDefault(); const v = new FormData(ev.currentTarget).get('q'); go(`#/web?tab=pages${v ? `&q=${enc(String(v))}` : ''}`); }} style={{ display: 'flex', gap: 8 }}>
        <input type="search" name="q" placeholder="Search path, title, URL" defaultValue={q || ''} /><button className="quiet">Filter</button>
      </form>
      <span style={{ flex: 1 }} />
      {can('manager') && <button className="quiet" onClick={() => newProperty(reload)}>New property</button>}
      {can('marketer') && <button onClick={() => (properties.length ? newPage(properties, reload) : newProperty(reload))}>Register a page</button>}
    </div>
    <p className="small muted">Properties: {properties.length ? properties.map((p: any, i: number) => <span key={p.id}>{i > 0 && ' · '}{p.name} <span className="mono">{p.base_url}</span> ({p.page_count})</span>) : 'none yet — create one first.'}</p>
    {pages.length ? (
      <div className="table-wrap"><table><thead><tr><th>Path</th><th>Page</th><th>Track</th><th>Status</th><th className="num">Views 30 d</th><th className="num">Submissions 30 d</th><th className="num">CTAs</th><th className="num">Forms</th><th /></tr></thead><tbody>
        {pages.map((p: any) => (
          <tr key={p.id} className={p.status === 'retired' ? 'muted' : ''}><td><span className="mono path">{p.path}</span><Sub>{p.property_name}</Sub></td>
            <td className="title-col"><a href={p.url} target="_blank" rel="noopener">{p.title}</a><Sub>{p.page_type_name || ''}{p.is_landing_page ? ' · landing page' : ''}{p.telemetry_enabled ? '' : ' · telemetry off'}{p.created_by_name ? ` · ${p.created_by_name}` : ''}</Sub></td>
            <td>{p.tid ? <Ref to={`#/tracks/${p.tid}`}>{p.tid}</Ref> : '—'}</td><td>{p.status}</td>
            <td className="num">{p.views_30d ?? 0}</td><td className="num">{p.submissions_30d ?? 0}</td><td className="num">{p.cta_count}</td><td className="num">{p.form_count}</td>
            {p.can_edit ? <td className="nowrap"><button type="button" className="link" onClick={() => editPage(p, reload)}>Edit</button> · <button type="button" className="link" onClick={() => removePage(p, reload)}>Remove</button></td> : <td />}</tr>
        ))}
      </tbody></table></div>
    ) : <Empty>No pages registered{q ? ' match' : ''}.</Empty>}
  </>);
}

function FormsTab() {
  const { can } = useApp();
  const { newForm } = useWebDialogs();
  const { data, error, reload } = useLoad(() => Promise.all([api('GET', '/web/forms'), api('GET', '/web/pages')]), []);
  if (error) return <Empty>{error}</Empty>;
  if (!data) return <Loading />;
  const [{ forms }, { pages }] = data;
  return (<>
    <div className="toolbar"><span style={{ flex: 1 }} />{can('marketer') && pages.length > 0 && <button onClick={() => newForm(pages, reload)}>Register a form</button>}</div>
    {forms.length ? (
      <div className="table-wrap"><table><thead><tr><th>Form</th><th>Id (for data-emm-form)</th><th>Page</th><th>Track</th><th>Submits via</th><th className="num">Submissions</th><th className="num">Failed relays</th><th>Last</th></tr></thead><tbody>
        {forms.map((f: any) => (
          <tr key={f.id} className={f.status === 'retired' ? 'muted' : ''}><td>{f.name}<Sub>{f.status}{f.purpose ? ` · ${f.purpose}` : ''}</Sub></td>
            <td className="small"><span className="mono path">{f.id}</span></td><td><span className="mono path">{f.path}</span></td><td>{f.tid ? <Ref to={`#/tracks/${f.tid}`}>{f.tid}</Ref> : '—'}</td>
            <td><code>{f.submit_method}</code>{f.hubspot_form_guid && <span className="sub mono">{f.hubspot_form_guid}</span>}</td>
            <td className="num"><a href={`#/web?tab=submissions`}>{f.submissions}</a></td><td className={`num ${f.failed_relays ? 'overdue' : ''}`}>{f.failed_relays}</td><td className="nowrap">{fmtWhen(f.last_submission_at)}</td></tr>
        ))}
      </tbody></table></div>
    ) : <Empty>No forms registered. {pages.length ? '' : 'Register the page first.'}</Empty>}
  </>);
}

export function LinkTable({ links, host, showRef = true, onChanged }: { links: any[]; host: string; showRef?: boolean; onChanged?: () => void }) {
  const { editLink, removeLink } = useWebDialogs();
  const reload = () => onChanged?.();
  const acts = (l: any) => (l.can_edit && onChanged ? <td className="nowrap"><button type="button" className="link" onClick={() => editLink(l, reload)}>Edit</button> · <button type="button" className="link" onClick={() => removeLink(l, reload)}>Remove</button></td> : <td />);
  return (
    <div className="table-wrap"><table><thead><tr><th>Link</th><th>Destination</th>{showRef && <th>For</th>}<th>UTM</th><th className="num">Clicks</th><th className="num">30 d</th><th>Created</th><th /></tr></thead><tbody>
      {links.map((l) => { const url = `https://${host}/go/${l.code}`; return (
        <tr key={l.id} className={l.is_active ? '' : 'muted'}><td className="nowrap"><span className="mono">/go/{l.code}</span><CopyLink url={url} />{l.label && <Sub>{l.label}</Sub>}</td>
          <td style={{ overflowWrap: 'anywhere' }}>{l.destination_url}</td>
          {showRef && <td>{l.tid ? <Ref to={`#/tracks/${l.tid}`}>{l.tid}</Ref> : <Ref to={`#/campaigns/${l.mid}`}>{l.mid}</Ref>}</td>}
          <td className="small">{l.utm_source} / {l.utm_medium}{l.utm_term ? ` / ${l.utm_term}` : ''}</td>
          <td className="num">{l.click_count ?? 0}</td><td className="num">{l.clicks_30d ?? 0}</td><td className="nowrap">{fmtDate(l.created_at)}<Sub>{l.created_by_name || ''}</Sub></td>{acts(l)}</tr>); })}
    </tbody></table></div>
  );
}

function LinksTab({ q }: { q?: string }) {
  const { can } = useApp();
  const { newLink } = useWebDialogs();
  const { data, error, reload } = useLoad(() => api('GET', `/web/links?${qs({ q, include_inactive: 1 })}`), [q]);
  if (error) return <Empty>{error}</Empty>;
  if (!data) return <Loading />;
  return (<>
    <div className="toolbar">
      <form onSubmit={(ev) => { ev.preventDefault(); const v = new FormData(ev.currentTarget).get('q'); go(`#/web?tab=links${v ? `&q=${enc(String(v))}` : ''}`); }} style={{ display: 'flex', gap: 8 }}>
        <input type="search" name="q" placeholder="Search code, label, MID, destination" defaultValue={q || ''} /><button className="quiet">Filter</button>
      </form>
      <span style={{ flex: 1 }} />
      {can('contributor') && <button onClick={() => newLink(reload)}>Create a link</button>}
    </div>
    {data.links.length ? <LinkTable links={data.links} host={data.public_hostname} onChanged={reload} /> : <Empty>No tracked links yet.</Empty>}
  </>);
}

function SubmissionsTab({ status }: { status?: string }) {
  const { can } = useApp();
  const { toast } = useFeedback();
  const { data, error, reload } = useLoad(() => api('GET', `/web/submissions?${qs({ status })}`), [status]);
  const retry = async (id: string) => { try { const r = await api('POST', `/web/submissions/${id}/retry`); toast(`Relay ${r.status ?? r.relay_status ?? 'done'}`, r.status === 'failed'); reload(); } catch (err) { toast((err as Error).message, true); } };
  return (<>
    <div className="toolbar">
      <Choice name="status" options={words(['pending', 'sent', 'failed', 'skipped'])} value={status} blank="Any relay status" onChange={(v) => go(`#/web?tab=submissions${v ? `&status=${v}` : ''}`)} />
    </div>
    {error ? <Empty>{error}</Empty> : !data ? <Loading /> : data.submissions.length ? (
      <div className="table-wrap"><table><thead><tr><th>When</th><th>Form</th><th>Email</th><th>Campaign</th><th>Source</th><th>Relay</th><th></th></tr></thead><tbody>
        {data.submissions.map((s: any) => (
          <tr key={s.id}><td className="nowrap">{fmtWhen(s.submitted_at)}</td><td>{s.form_name}<span className="sub mono">{s.path}</span></td><td>{s.email || '—'}</td>
            <td>{s.tid ? <Ref to={`#/tracks/${s.tid}`}>{s.tid}</Ref> : s.mid ? <Ref to={`#/campaigns/${s.mid}`}>{s.mid}</Ref> : '—'}</td>
            <td className="small">{[s.utm_source, s.utm_medium].filter(Boolean).join(' / ') || s.referrer || '—'}</td>
            <td><span className={`status ${s.relay_status === 'sent' ? 'connected' : s.relay_status === 'failed' ? 'error' : 'planned'}`}>{s.relay_status}</span>{s.relay_error && <span className="sub overdue">{s.relay_error}</span>}{s.hubspot_contact_id && <span className="sub mono">contact {s.hubspot_contact_id}</span>}</td>
            <td>{can('manager') && ['failed', 'pending'].includes(s.relay_status) && <button className="quiet" onClick={() => retry(s.id)}>Retry</button>}</td></tr>
        ))}
      </tbody></table></div>
    ) : <Empty>No submissions{status ? ` with relay status ${status}` : ''}.</Empty>}
  </>);
}

function SnippetTab() {
  const { data, error } = useLoad(() => api('GET', '/web/snippet'), []);
  if (error) return <Empty>{error}</Empty>;
  if (!data) return <Loading />;
  const Block = ({ label, code }: { label: string; code: string }) => (
    <div className="field"><label>{label} <CopyLink url={code} /></label><textarea className="snippet" readOnly value={code} rows={Math.min(6, code.split('\n').length + 1)} /></div>
  );
  return (<section>
    <Block label="Script tag (every registered page)" code={data.script} />
    <Block label="Consent-gated variant" code={data.consent_gated} />
    <Block label="Count a call to action" code={data.cta} />
    <Block label="Relay a form" code={data.form} />
    <ul className="small muted">{data.notes.map((n: string) => <li key={n}>{n}</li>)}</ul>
  </section>);
}

/** "Links & pages" block on campaign and track detail. */
export function LinksAndPages(props: { kind: 'campaign'; campaign: any; tracks: any[] } | { kind: 'track'; track: any }) {
  const { can } = useApp();
  const { newLink, newPage, editPage, removePage } = useWebDialogs();
  const isTrack = props.kind === 'track';
  const entity = isTrack ? props.track : props.campaign;
  const { data, error, reload } = useLoad(() => Promise.all([
    api('GET', `/web/links?${qs(isTrack ? { track: entity.id } : { campaign: entity.id })}`),
    api('GET', `/web/pages?${qs(isTrack ? { track: entity.id } : { campaign: entity.id })}`),
    api('GET', '/web/properties'),
  ]), [entity.id]);
  const head = (<div className="page-head" style={{ marginBottom: 8 }}><h2>Links &amp; pages</h2>
    <div className="chips">
      {can('marketer') && data && data[2].properties.length > 0 && isTrack && <button className="quiet" onClick={() => newPage(data[2].properties, reload, entity.tid)}>Register a page</button>}
      {can('contributor') && <button className="quiet" onClick={() => newLink(() => reload(), isTrack ? { tid: entity.tid } : { mid: entity.mid })}>Create link</button>}
    </div></div>);
  if (error) return <section>{head}<p className="muted small">{error}</p></section>;
  if (!data) return <section>{head}<p className="muted small">Loading…</p></section>;
  const [{ links, public_hostname }, { pages }] = data;
  return (
    <section>{head}
      {links.length ? <LinkTable links={links} host={public_hostname} showRef={!isTrack} onChanged={reload} /> : <p className="muted small">No tracked links yet. A link carries {isTrack ? entity.tid : entity.mid} into UTM parameters so clicks and form fills land here.</p>}
      {pages.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 10 }}><table><thead><tr><th>Registered page</th>{!isTrack && <th>Track</th>}<th>Status</th><th className="num">Views 30 d</th><th className="num">Submissions 30 d</th><th className="num">Forms</th><th /></tr></thead><tbody>
          {pages.map((p: any) => <tr key={p.id}><td><a href={p.url} target="_blank" rel="noopener">{p.title}</a><span className="sub mono">{p.path}</span>{p.created_by_name && <Sub>{p.created_by_name}</Sub>}</td>
            {!isTrack && <td>{p.tid ? <Ref to={`#/tracks/${p.tid}`}>{p.tid}</Ref> : '—'}</td>}<td>{p.status}</td>
            <td className="num">{p.views_30d ?? 0}</td><td className="num">{p.submissions_30d ?? 0}</td><td className="num">{p.form_count}</td>
            {p.can_edit ? <td className="nowrap"><button type="button" className="link" onClick={() => editPage(p, reload)}>Edit</button> · <button type="button" className="link" onClick={() => removePage(p, reload)}>Remove</button></td> : <td />}</tr>)}
        </tbody></table></div>
      )}
    </section>
  );
}
